import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, DollarSign, Hash, Calculator } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import type { AccountMetrics } from '@/types/account-drilldown';

interface AccountMetricsCardsProps {
  metrics: AccountMetrics | undefined;
  currency: string;
  isLoading: boolean;
}

export function AccountMetricsCards({ metrics, currency, isLoading }: AccountMetricsCardsProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const cards = [
    {
      title: 'Total Income',
      value: metrics ? formatCurrency(metrics.totalIncome) : formatCurrency(0),
      icon: TrendingUp,
      color: 'text-green-600',
    },
    {
      title: 'Total Expenses',
      value: metrics ? formatCurrency(metrics.totalExpenses) : formatCurrency(0),
      icon: TrendingDown,
      color: 'text-red-600',
    },
    {
      title: 'Net Change',
      value: metrics ? formatCurrency(metrics.netChange) : formatCurrency(0),
      icon: DollarSign,
      color: metrics && metrics.netChange >= 0 ? 'text-green-600' : 'text-red-600',
    },
    {
      title: 'Transaction Count',
      value: metrics ? metrics.transactionCount.toString() : '0',
      icon: Hash,
      color: 'text-blue-600',
    },
    {
      title: 'Average Transaction',
      value: metrics ? formatCurrency(metrics.averageTransaction) : formatCurrency(0),
      icon: Calculator,
      color: 'text-purple-600',
    },
  ];

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {cards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4 rounded" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5" role="region" aria-label="Account metrics summary">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {card.title}
            </CardTitle>
            <card.icon className={`h-4 w-4 ${card.color}`} aria-hidden="true" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" aria-label={`${card.title}: ${card.value}`}>
              {card.value}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
